import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest
} from 'redux-saga/effects'
import feathersClient from 'utils/feathers'
import {actionTypes as lmsActionTypes} from 'lms/store/constants'
import {actionTypes as userActionTypes} from 'users/store/constants'
import {actionTypes as rfpActionTypes} from 'rfps/store/constants'
import {actionTypes} from './constants'
import {getCurrentUser} from 'users/store/selectors'
import {getMode} from 'app/store/selectors'
import {fetchLeads} from './actions'

const leadsService = feathersClient.service('leads')

function * generateLeadsSaga (action) {
  const state = yield select()
  const user = getCurrentUser(state)
  const mode = getMode(state)

  // don't generate leads for admins & vendors
  if (mode !== 'user') return

  // don't generate leads for students
  if (user && user.organizationTypeRole === 'student') return

  let appIds, trigger, comparedSystems
  switch (action.type) {
    case lmsActionTypes.FETCH_APPLICATION_DETAIL_REQUESTED:
      appIds = [action.payload.id]
      trigger = 'Details Viewed'
      break
    case lmsActionTypes.VIEW_APPLICATION_CONTACT_INFO:
      appIds = [action.payload.id]
      trigger = 'Contact Info Viewed'
      break
    case lmsActionTypes.WEBSITE_LINK_VISITED:
      appIds = [action.payload.id]
      trigger = 'Website Visited'
      break
    case userActionTypes.ADD_LMS_TO_LIST:
      appIds = action.payload
      trigger = 'Added to Favorites'
      break
    case rfpActionTypes.CREATE_RFP_SUCCEEDED:
      appIds = [action.payload.systemId]
      trigger = 'Request Information'
      break
    case lmsActionTypes.COMPARE_SYSTEMS:
      appIds = action.payload.ids ? [...action.payload.ids] : []
      trigger = 'Compared'
      comparedSystems = action.payload.ids ? [...action.payload.ids] : []
      break
    case lmsActionTypes.SCHEDULE_DEMO_CLICKED:
      appIds = [action.payload.id]
      trigger = 'Schedule Demo'
      break
    // no default
  }

  for (const appId of appIds) {
    let systemsBeingCompared
    if (comparedSystems) {
      systemsBeingCompared = comparedSystems.filter(id => id !== appId)
    }

    // Query directly for this user+application pair to avoid pagination caps
    const {data} = yield call([leadsService, 'find'], {
      query: {version: 2, user, application: appId, $limit: 1}
    })
    let lead = data[0] || null

    if (!lead) {
      // create new lead
      lead = yield call([leadsService, 'create'], {
        version: 2,
        user,
        application: appId,
        otherSystems: comparedSystems ? systemsBeingCompared : null
      })
    }
    // update lead with new action
    lead = yield call([leadsService, 'patch'], lead.id || lead._id, {
      $addToSet: {
        actions: {
          trigger,
          otherSystems: comparedSystems ? systemsBeingCompared : null
        }
      }
    })
  }
}

function * fetchLeadsSaga (action) {
  const {limit, skip} = action.payload
  // DAYS * HOURS * MINUTES * SECONDS + MILLISECONDS
  const DAY_MS = 90 * 24 * 60 * 60 * 1000
  try {
    const query = {version: 2, $limit: limit, $skip: skip, 
      updatedAt: {
        $gt: new Date().getTime() - DAY_MS
      }
    }
    //console.log('query = ', query);

    const results = yield call([leadsService, 'find'], {
      query: query
    })

    const fetchedCount = skip + results.data.length
    const completed = fetchedCount >= results.total

    yield put({
      type: actionTypes.FETCH_LEADS_SUCCEEDED,
      payload: {results, completed}
    })

    if (!completed) yield put(fetchLeads({limit, skip: fetchedCount}))
  } catch (error) {
    yield put({type: actionTypes.FETCH_LEADS_FAILED, payload: {error}})
  }
}

function * watchGenerateLeads () {
  const leadGenerators = [
    lmsActionTypes.FETCH_APPLICATION_DETAIL_REQUESTED,
    lmsActionTypes.VIEW_APPLICATION_CONTACT_INFO,
    lmsActionTypes.WEBSITE_LINK_VISITED,
    userActionTypes.ADD_LMS_TO_LIST,
    rfpActionTypes.CREATE_RFP_SUCCEEDED,
    lmsActionTypes.COMPARE_SYSTEMS,
    lmsActionTypes.SCHEDULE_DEMO_CLICKED
  ]

  yield takeEvery(leadGenerators, generateLeadsSaga)
}

function * watchFetchLeads () {
  yield takeLatest(actionTypes.FETCH_LEADS_REQUESTED, fetchLeadsSaga)
}

function * rootSaga () {
  yield all([watchGenerateLeads(), watchFetchLeads()])
}

export default rootSaga
